import { API_URL } from '../config';
import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart3,
  RefreshCw,
  FileText,
  AlertCircle,
  CheckCircle,
  Clock,
  TrendingUp,
  Phone,
  AlertTriangle,
  UserX,
  X,
  ExternalLink,
  DollarSign,
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';
import StatCard from '../components/StatCard';
import DonutChart from '../components/DonutChart';


// Color maps
const STATUS_COLORS = {
  ABERTO: '#f39c12',
  EM_ANDAMENTO: '#3498db',
  AGUARDANDO_RETORNO: '#e67e22',
  AGUARDANDO_OUTRO_SETOR: '#9b59b6',
  FINALIZADO: '#27ae60',
  TRANSFERIDO: '#95a5a6',
};

const CANAL_COLORS = {
  TELEFONICO: '#3498db',
  EMAIL: '#e74c3c',
  PRESENCIAL: '#27ae60',
  WHATSAPP: '#25D366',
  CHAT: '#9b59b6',
  OUTRO: '#95a5a6',
};

const PRIORIDADE_COLORS = {
  NORMAL: '#27ae60',
  ALTA: '#f39c12',
  URGENTE: '#e74c3c',
};

// Helper: format hours
const formatarHoras = (horas) => {
  if (horas === null || horas === undefined) return '-';
  if (horas === 0) return '0h';
  if (horas < 1) return `${Math.round(horas * 60)}min`;
  const h = Math.floor(horas);
  const m = Math.round((horas - h) * 60);
  if (m === 0) return `${h}h`;
  return `${h}h ${m}min`;
};

// Helper: convert object distribution { KEY: count } to donut data array
const buildDonutData = (dist, colorMap, total) => {
  if (!dist || total === 0) return [];
  return Object.entries(dist)
    .filter(([, v]) => v > 0)
    .map(([key, value]) => ({
      label: key.replace(/_/g, ' '),
      value: Math.round((value / total) * 100),
      rawCount: value,
      color: colorMap[key] || '#95a5a6',
    }));
};

// Section header component
const SectionHeader = ({ title, icon }) => (
  <div style={styles.sectionHeader}>
    <div style={styles.sectionHeaderBorder} />
    <div style={styles.sectionHeaderContent}>
      {icon}
      <h2 style={styles.sectionTitle}>{title}</h2>
    </div>
  </div>
);

// Horizontal bar row for por_setor / por_categoria
const HBarRow = ({ label, total, maxTotal, abertos, finalizados, color }) => {
  const pct = maxTotal > 0 ? (total / maxTotal) * 100 : 0;
  const abertosPct = total > 0 ? (abertos / total) * 100 : 0;
  const finalizadosPct = total > 0 ? (finalizados / total) * 100 : 0;
  return (
    <div style={styles.hbarRow}>
      <div style={styles.hbarLabel} title={label}>{label}</div>
      <div style={styles.hbarTrack}>
        <div style={{ ...styles.hbarFill, width: `${pct}%` }}>
          {finalizadosPct > 0 && (
            <div style={{ ...styles.hbarSegment, width: `${finalizadosPct}%`, backgroundColor: color || '#27ae60' }} />
          )}
          {abertosPct > 0 && (
            <div style={{ ...styles.hbarSegment, width: `${abertosPct}%`, backgroundColor: '#f39c12' }} />
          )}
        </div>
      </div>
      <div style={styles.hbarTotal}>{total}</div>
    </div>
  );
};

const DashboardAtendimentos = () => {
  const [loading, setLoading] = useState(true);
  const [dados, setDados] = useState(null);
  const [dias, setDias] = useState(30);
  const [setorId, setSetorId] = useState('');
  const [natureza, setNatureza] = useState('');   // '' = atendimentos + tarefas
  const [setores, setSetores] = useState([]);
  const [modalSemContato, setModalSemContato] = useState(null);

  const userRaw = localStorage.getItem('user');
  const user = userRaw ? JSON.parse(userRaw) : null;
  const perfil = user?.perfil || '';
  const podeVerSetores = perfil === 'administrador' || user?.ver_todos_dashboard;

  // Load sectors for filter
  useEffect(() => {
    if (podeVerSetores) {
      const token = localStorage.getItem('token');
      axios
        .get(`${API_URL}/setores/`, {
          params: { ativos_apenas: true },
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => setSetores(Array.isArray(res.data) ? res.data : []))
        .catch(() => {});
    }
  }, [podeVerSetores]);

  const carregarDados = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = { dias };
      if (setorId) params.setor_id = setorId;
      if (natureza) params.natureza = natureza;

      const res = await axios.get(`${API_URL}/dashboard/atendimentos`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setDados(res.data);
    } catch (error) {
      toast.error('Erro ao carregar dados do dashboard');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [dias, setorId, natureza]);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  // ── Derived data ───────────────────────────────────────────────────────────

  const resumo = dados?.resumo || {};
  const totalAtendimentos = resumo.total_atendimentos || 0;
  const abertosAgora = resumo.total_abertos || 0;
  const finalizados = resumo.total_finalizados || 0;
  const tempoMedio = resumo.tempo_medio_atendimento_horas ?? null;

  // Helper: convert backend array [{key_field: X, total: Y}] to object {X: Y}
  const arrayToDist = (arr, keyField) => {
    if (!Array.isArray(arr)) return {};
    return arr.reduce((acc, item) => {
      if (item[keyField]) acc[item[keyField]] = item.total || 0;
      return acc;
    }, {});
  };

  // Por Status donut
  const porStatusDist = arrayToDist(dados?.por_status, 'status');
  const statusDonut = buildDonutData(porStatusDist, STATUS_COLORS, totalAtendimentos);

  // Por Canal donut
  const porCanalDist = arrayToDist(dados?.por_canal, 'canal');
  const canalTotal = Object.values(porCanalDist).reduce((a, b) => a + b, 0);
  const canalDonut = buildDonutData(porCanalDist, CANAL_COLORS, canalTotal || 1);

  // Por Prioridade donut
  const porPrioridadeDist = arrayToDist(dados?.por_prioridade, 'prioridade');
  const prioridadeTotal = Object.values(porPrioridadeDist).reduce((a, b) => a + b, 0);
  const prioridadeDonut = buildDonutData(porPrioridadeDist, PRIORIDADE_COLORS, prioridadeTotal || 1);

  // SLA
  const sla = dados?.sla || {};
  const slaTotal = (sla.dentro_sla || 0) + (sla.fora_sla || 0);
  const slaPct = slaTotal > 0 ? Math.round(((sla.dentro_sla || 0) / slaTotal) * 100) : null;

  // Por Setor
  const porSetor = Array.isArray(dados?.por_setor) ? dados.por_setor : [];
  const maxSetorTotal = porSetor.reduce((m, s) => Math.max(m, s.total || 0), 0);

  // Por Categoria
  const porCategoria = Array.isArray(dados?.por_categoria) ? dados.por_categoria : [];
  const maxCatTotal = porCategoria.reduce((m, c) => Math.max(m, c.total || 0), 0);

  // Por Colaborador
  const porColaborador = Array.isArray(dados?.por_colaborador)
    ? [...dados.por_colaborador].sort((a, b) => (b.total || 0) - (a.total || 0))
    : [];

  // Volume por Dia
  const volumeDia = Array.isArray(dados?.por_dia) ? dados.por_dia : [];
  const maxVolume = volumeDia.reduce((m, d) => Math.max(m, d.total || 0), 0);

  // Recorrência / top beneficiários
  const topBeneficiarios = Array.isArray(dados?.recorrencia)
    ? dados.recorrencia
    : [];

  // Transferências
  const transferencias = Array.isArray(dados?.transferencias) ? dados.transferencias : [];

  // Tempo por estágio Kanban
  const tempoKanban = Array.isArray(dados?.tempo_por_coluna) ? dados.tempo_por_coluna : [];

  // ── Loading state ──────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <RefreshCw size={32} color="var(--brand, #20a490)" style={{ animation: 'spin 1s linear infinite' }} />
        <p style={{ color: '#666', marginTop: '12px' }}>Carregando dashboard...</p>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={styles.container}>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      {/* ── Header ─────────────────────────────────────────────────── */}
      <div style={styles.header}>
        <div style={styles.headerTitle}>
          <BarChart3 size={28} color="var(--brand, #20a490)" />
          <div>
            <h1 style={styles.titulo}>Dashboard de Atendimentos</h1>
            <p style={styles.subtitulo}>Visão geral dos atendimentos e indicadores operacionais</p>
          </div>
        </div>

        <div style={styles.filtros}>
          {/* Period buttons */}
          <div style={styles.periodButtons}>
            {[7, 15, 30, 60, 90].map((d) => (
              <button
                key={d}
                onClick={() => setDias(d)}
                style={{
                  ...styles.periodBtn,
                  backgroundColor: dias === d ? 'var(--brand, #20a490)' : 'white',
                  color: dias === d ? 'white' : '#555',
                  borderColor: dias === d ? 'var(--brand, #20a490)' : '#ddd',
                }}
              >
                {d}d
              </button>
            ))}
          </div>

          {/* Natureza: atendimentos (beneficiário) × tarefas (internas) */}
          <div style={styles.periodButtons} title="Atendimento é virado ao beneficiário; tarefa é trabalho interno (régua, correções). Métricas só de beneficiário (canal, contato) ficam vazias em Tarefas.">
            {[['', 'Ambos'], ['atendimento', 'Atendimentos'], ['tarefa', 'Tarefas']].map(([v, r]) => (
              <button
                key={v || 'ambos'}
                onClick={() => setNatureza(v)}
                style={{
                  ...styles.periodBtn,
                  backgroundColor: natureza === v ? 'var(--brand, #20a490)' : 'white',
                  color: natureza === v ? 'white' : '#555',
                  borderColor: natureza === v ? 'var(--brand, #20a490)' : '#ddd',
                }}
              >
                {r}
              </button>
            ))}
          </div>

          {/* Sector filter */}
          {podeVerSetores ? (
            <select
              value={setorId}
              onChange={(e) => setSetorId(e.target.value)}
              style={styles.select}
            >
              <option value="">Todos os setores</option>
              {setores.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nome}
                </option>
              ))}
            </select>
          ) : (
            <span style={{ fontSize: '0.8rem', color: '#888', padding: '6px 12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
              Filtrado: seus setores
            </span>
          )}

          <button onClick={carregarDados} style={styles.btnRefresh}>
            <RefreshCw size={16} />
            Atualizar
          </button>
        </div>
      </div>

      {/* ── Row 1: Summary StatCards ────────────────────────────────── */}
      <div style={styles.statsGrid}>
        <StatCard
          title="Total Atendimentos"
          value={totalAtendimentos}
          icon={<FileText size={24} />}
          color="#3498db"
          subtitle={`Últimos ${dias} dias`}
        />
        <StatCard
          title="Abertos Agora"
          value={abertosAgora}
          icon={<AlertCircle size={24} />}
          color="#f39c12"
        />
        <StatCard
          title="Finalizados"
          value={finalizados}
          icon={<CheckCircle size={24} />}
          color="#27ae60"
          subtitle={`Últimos ${dias} dias`}
        />
        <StatCard
          title="Tempo Médio"
          value={tempoMedio !== null ? formatarHoras(tempoMedio) : '-'}
          icon={<Clock size={24} />}
          color="#9b59b6"
          subtitle="até finalização"
        />
        <StatCard
          title="Sem Contato"
          value={dados?.sem_contato?.abertos_agora || 0}
          icon={<UserX size={24} />}
          color="#e74c3c"
          subtitle="abertos sem tel/email"
          pulse={(dados?.sem_contato?.abertos_agora || 0) > 0}
        />
        <StatCard
          title="Tel. Atualizados"
          value={dados?.telefones_atualizados_whatsapp || 0}
          icon={<Phone size={24} />}
          color="#25D366"
          subtitle="via WhatsApp"
        />
        {natureza === 'tarefa' && (
          <StatCard
            title="R$ em análise"
            value={Number(dados?.resumo?.valor_em_analise || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })}
            icon={<DollarSign size={24} />}
            color="#b9770e"
            subtitle={`${dados?.resumo?.tarefas_com_valor || 0} tarefa(s) aberta(s) com valor`}
          />
        )}
      </div>

      {/* ── Row 2: Por Status + Por Canal ───────────────────────────── */}
      <div style={styles.twoCol}>
        {/* Por Status */}
        <div style={styles.card}>
          <SectionHeader title="Por Status" icon={<BarChart3 size={18} color="var(--brand, #20a490)" />} />
          {statusDonut.length === 0 ? (
            <EmptyState text="Sem dados de status" />
          ) : (
            <div style={styles.donutWrapper}>
              <DonutChart
                data={statusDonut}
                size={180}
                strokeWidth={28}
                centerText={`${totalAtendimentos}`}
                centerSubtext="total"
              />
            </div>
          )}
        </div>

        {/* Por Canal */}
        <div style={styles.card}>
          <SectionHeader title="Por Canal" icon={<BarChart3 size={18} color="var(--brand, #20a490)" />} />
          {canalDonut.length === 0 ? (
            <EmptyState text="Sem dados de canal" />
          ) : (
            <div style={styles.donutWrapper}>
              <DonutChart
                data={canalDonut}
                size={180}
                strokeWidth={28}
                centerText={`${canalTotal}`}
                centerSubtext="atend."
              />
            </div>
          )}
        </div>
      </div>

      {/* ── Row 3: Por Prioridade + SLA ─────────────────────────────── */}
      <div style={styles.twoCol}>
        {/* Por Prioridade */}
        <div style={styles.card}>
          <SectionHeader title="Por Prioridade" icon={<AlertCircle size={18} color="var(--brand, #20a490)" />} />
          {prioridadeDonut.length === 0 ? (
            <EmptyState text="Sem dados de prioridade" />
          ) : (
            <div style={styles.donutWrapper}>
              <DonutChart
                data={prioridadeDonut}
                size={180}
                strokeWidth={28}
                centerText={`${prioridadeTotal}`}
                centerSubtext="total"
              />
            </div>
          )}
        </div>

        {/* SLA */}
        <div style={styles.card}>
          <SectionHeader title="SLA: Cumprimento de Prazo" icon={<Clock size={18} color="var(--brand, #20a490)" />} />
          {slaPct === null ? (
            <EmptyState text="Sem dados de SLA" />
          ) : (
            <div style={styles.slaContent}>
              <div style={{ ...styles.slaBigNumber, color: slaPct >= 80 ? '#27ae60' : slaPct >= 60 ? '#f39c12' : '#e74c3c' }}>
                {slaPct}%
              </div>
              <p style={styles.slaLabel}>dentro do SLA</p>

              <div style={styles.slaBarTrack}>
                <div
                  style={{
                    ...styles.slaBarFill,
                    width: `${slaPct}%`,
                    backgroundColor: slaPct >= 80 ? '#27ae60' : slaPct >= 60 ? '#f39c12' : '#e74c3c',
                  }}
                />
              </div>

              <div style={styles.slaCountRow}>
                <div style={styles.slaCountItem}>
                  <div style={{ ...styles.slaCountDot, backgroundColor: '#27ae60' }} />
                  <span style={styles.slaCountLabel}>Dentro do SLA</span>
                  <span style={styles.slaCountValue}>{sla.dentro_sla || 0}</span>
                </div>
                <div style={styles.slaCountItem}>
                  <div style={{ ...styles.slaCountDot, backgroundColor: '#e74c3c' }} />
                  <span style={styles.slaCountLabel}>Fora do SLA</span>
                  <span style={styles.slaCountValue}>{sla.fora_sla || 0}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Row 4: Por Setor ─────────────────────────────────────────── */}
      <div style={styles.card}>
        <SectionHeader title="Por Setor" icon={<BarChart3 size={18} color="var(--brand, #20a490)" />} />
        {porSetor.length === 0 ? (
          <EmptyState text="Sem dados por setor" />
        ) : (
          <div style={styles.hbarContainer}>
            <div style={styles.hbarLegend}>
              <span style={styles.hbarLegendItem}>
                <span style={{ ...styles.hbarLegendDot, backgroundColor: '#27ae60' }} /> Finalizados
              </span>
              <span style={styles.hbarLegendItem}>
                <span style={{ ...styles.hbarLegendDot, backgroundColor: '#f39c12' }} /> Abertos
              </span>
            </div>
            {porSetor.map((s, i) => (
              <HBarRow
                key={i}
                label={s.setor || s.nome || `Setor ${i + 1}`}
                total={s.total || 0}
                maxTotal={maxSetorTotal}
                abertos={s.abertos || 0}
                finalizados={s.finalizados || 0}
                color="#27ae60"
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Row 5: Por Categoria ─────────────────────────────────────── */}
      <div style={styles.card}>
        <SectionHeader title="Por Categoria" icon={<BarChart3 size={18} color="var(--brand, #20a490)" />} />
        {porCategoria.length === 0 ? (
          <EmptyState text="Sem dados por categoria" />
        ) : (
          <div style={styles.hbarContainer}>
            <div style={styles.hbarLegend}>
              <span style={styles.hbarLegendItem}>
                <span style={{ ...styles.hbarLegendDot, backgroundColor: '#27ae60' }} /> Finalizados
              </span>
              <span style={styles.hbarLegendItem}>
                <span style={{ ...styles.hbarLegendDot, backgroundColor: '#f39c12' }} /> Abertos
              </span>
            </div>
            {porCategoria.map((c, i) => (
              <HBarRow
                key={i}
                label={c.categoria || c.nome || `Categoria ${i + 1}`}
                total={c.total || 0}
                maxTotal={maxCatTotal}
                abertos={c.abertos || 0}
                finalizados={c.finalizados || 0}
                color={c.cor || 'var(--brand, #20a490)'}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Row 6: Por Colaborador ───────────────────────────────────── */}
      <div style={styles.card}>
        <SectionHeader title="Por Colaborador" icon={<TrendingUp size={18} color="var(--brand, #20a490)" />} />
        {porColaborador.length === 0 ? (
          <EmptyState text="Sem dados por colaborador" />
        ) : (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Colaborador</th>
                  <th style={{ ...styles.th, textAlign: 'center' }}>Total</th>
                  <th style={{ ...styles.th, textAlign: 'center' }}>Finalizados</th>
                  <th style={{ ...styles.th, textAlign: 'center' }}>Tempo Médio</th>
                </tr>
              </thead>
              <tbody>
                {porColaborador.map((c, i) => (
                  <tr key={i} style={i % 2 === 0 ? styles.trEven : styles.trOdd}>
                    <td style={styles.td}>{c.colaborador || c.nome || '-'}</td>
                    <td style={{ ...styles.td, textAlign: 'center', fontWeight: '600', color: '#3498db' }}>
                      {c.total || 0}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'center', color: '#27ae60' }}>
                      {c.finalizados || 0}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'center', color: '#9b59b6' }}>
                      {formatarHoras(c.tempo_medio_horas)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Row: Volume por Dia e Qualidade do Cadastro ─────────────────────────────── */}
      <div style={styles.card}>
        <SectionHeader title="Volume por Dia e Qualidade do Cadastro" icon={<BarChart3 size={18} color="var(--brand, #20a490)" />} />
        {(() => {
          const evolucaoContato = dados?.sem_contato?.evolucao || [];
          if (evolucaoContato.length === 0) {
            return <EmptyState text="Sem dados de contato" />;
          }
          const maxTotalContato = evolucaoContato.reduce((m, d) => Math.max(m, d.total || 0), 0);
          return (
            <div>
              {/* Resumo */}
              <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{ width: 12, height: 12, borderRadius: 2, background: '#27ae60' }} />
                  <span style={{ fontSize: '0.85rem', color: '#666' }}>Com contato</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{ width: 12, height: 12, borderRadius: 2, background: '#e74c3c' }} />
                  <span style={{ fontSize: '0.85rem', color: '#666' }}>Sem contato (tel/email)</span>
                </div>
              </div>
              {/* Grafico de barras empilhadas */}
              <div style={styles.barChartContainer}>
                <div style={styles.barChart}>
                  {evolucaoContato.slice().reverse().map((d, i) => {
                    const totalPct = maxTotalContato > 0 ? (d.total / maxTotalContato) * 100 : 0;
                    const comTelPct = d.total > 0 ? (d.com_telefone / d.total) * 100 : 0;
                    const semTelPct = d.total > 0 ? (d.sem_contato / d.total) * 100 : 0;
                    const dateLabel = d.data
                      ? new Date(d.data + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
                      : '';
                    return (
                      <div key={i} style={styles.barCol}>
                        <div style={styles.barColInner}>
                          <span style={styles.barValue}>{d.total > 0 ? d.total : ''}</span>
                          <div
                            style={{
                              ...styles.bar,
                              height: `${Math.max(totalPct, d.total > 0 ? 4 : 0)}%`,
                              display: 'flex',
                              flexDirection: 'column',
                              background: 'transparent',
                            }}
                            title={`${dateLabel}: ${d.com_telefone} com contato, ${d.sem_contato} sem contato`}
                          >
                            {comTelPct > 0 && (
                              <div style={{ flex: comTelPct, background: '#27ae60', borderRadius: '3px 3px 0 0', minHeight: 2 }} />
                            )}
                            {semTelPct > 0 && (
                              <div
                                style={{
                                  flex: semTelPct,
                                  background: '#e74c3c',
                                  borderRadius: '0 0 3px 3px',
                                  minHeight: 2,
                                  cursor: d.sem_contato > 0 ? 'pointer' : 'default',
                                  transition: 'opacity 0.2s',
                                }}
                                onClick={() => {
                                  if (d.sem_contato > 0) {
                                    const beneficiarios = (d.nomes_sem_contato || []).map((nome, idx) => ({
                                      nome,
                                      cpf: d.cpfs_sem_contato?.[idx] || '',
                                      matricula: d.matriculas_sem_contato?.[idx] || '',
                                    }));
                                    setModalSemContato({ data: dateLabel, beneficiarios });
                                  }
                                }}
                                onMouseEnter={(e) => { if (d.sem_contato > 0) e.target.style.opacity = '0.7'; }}
                                onMouseLeave={(e) => { e.target.style.opacity = '1'; }}
                              />
                            )}
                          </div>
                        </div>
                        <span style={styles.barLabel}>{dateLabel}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* ── Row 8: Recorrência + Transferências ─────────────────────── */}
      <div style={styles.twoCol}>
        {/* Top Beneficiários */}
        <div style={styles.card}>
          <SectionHeader title="Recorrência: Top Beneficiários" icon={<TrendingUp size={18} color="var(--brand, #20a490)" />} />
          {topBeneficiarios.length === 0 ? (
            <EmptyState text="Sem dados de recorrência" />
          ) : (
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>#</th>
                    <th style={styles.th}>Nome</th>
                    <th style={styles.th}>CPF</th>
                    <th style={{ ...styles.th, textAlign: 'center' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {topBeneficiarios.slice(0, 10).map((b, i) => (
                    <tr key={i} style={i % 2 === 0 ? styles.trEven : styles.trOdd}>
                      <td style={{ ...styles.td, color: '#999', width: '32px' }}>{i + 1}</td>
                      <td style={styles.td}>{b.beneficiario || '-'}</td>
                      <td style={{ ...styles.td, fontFamily: 'monospace', color: '#666' }}>{b.cpf || '-'}</td>
                      <td style={{ ...styles.td, textAlign: 'center', fontWeight: '600', color: '#3498db' }}>
                        {b.total_atendimentos || 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Transferências entre Setores */}
        <div style={styles.card}>
          <SectionHeader title="Transferências entre Setores" icon={<BarChart3 size={18} color="var(--brand, #20a490)" />} />
          {transferencias.length === 0 ? (
            <EmptyState text="Sem dados de transferências" />
          ) : (
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Origem</th>
                    <th style={{ ...styles.th, textAlign: 'center', padding: '0' }}>→</th>
                    <th style={styles.th}>Destino</th>
                    <th style={{ ...styles.th, textAlign: 'center' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {transferencias.map((t, i) => (
                    <tr key={i} style={i % 2 === 0 ? styles.trEven : styles.trOdd}>
                      <td style={styles.td}>{t.setor_origem || t.origem || '-'}</td>
                      <td style={{ ...styles.td, textAlign: 'center', color: '#999' }}>→</td>
                      <td style={styles.td}>{t.setor_destino || t.destino || '-'}</td>
                      <td style={{ ...styles.td, textAlign: 'center', fontWeight: '600', color: '#9b59b6' }}>
                        {t.total || 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Row 9: Tempo por Estágio Kanban ─────────────────────────── */}
      <div style={styles.card}>
        <SectionHeader title="Tempo por Estágio Kanban" icon={<Clock size={18} color="var(--brand, #20a490)" />} />
        {tempoKanban.length === 0 ? (
          <EmptyState text="Sem dados de tempo por estágio" />
        ) : (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Setor</th>
                  <th style={styles.th}>Coluna / Estágio</th>
                  <th style={{ ...styles.th, textAlign: 'center' }}>Tempo Médio</th>
                </tr>
              </thead>
              <tbody>
                {tempoKanban.map((t, i) => (
                  <tr key={i} style={i % 2 === 0 ? styles.trEven : styles.trOdd}>
                    <td style={styles.td}>{t.setor || '-'}</td>
                    <td style={styles.td}>{t.coluna || t.estagio || '-'}</td>
                    <td style={{ ...styles.td, textAlign: 'center', color: '#9b59b6', fontWeight: '600' }}>
                      {formatarHoras(t.tempo_medio_horas)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* ── Modal: Beneficiários Sem Contato ──────────────────────── */}
      {modalSemContato && (
        <div style={modalStyles.overlay} onClick={() => setModalSemContato(null)}>
          <div style={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={modalStyles.header}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <UserX size={20} color="#e74c3c" />
                <span style={{ fontWeight: '600', fontSize: '16px', color: '#333' }}>
                  Sem Contato em {modalSemContato.data}
                </span>
                <span style={modalStyles.badge}>
                  {modalSemContato.beneficiarios.length}
                </span>
              </div>
              <button style={modalStyles.closeBtn} onClick={() => setModalSemContato(null)}>
                <X size={18} />
              </button>
            </div>
            <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#888' }}>
              Beneficiários com atendimentos nesta data sem telefone e sem e-mail cadastrado.
            </p>
            <div style={modalStyles.listContainer}>
              {modalSemContato.beneficiarios.map((b, i) => (
                <div
                  key={i}
                  style={{
                    ...modalStyles.row,
                    background: i % 2 === 0 ? '#fafafa' : '#fff',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: '600', color: '#333', fontSize: '14px' }}>{b.nome || '—'}</div>
                    <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
                      {b.cpf ? `CPF: ${b.cpf}` : ''}
                      {b.cpf && b.matricula ? ' · ' : ''}
                      {b.matricula ? `Mat: ${b.matricula}` : ''}
                    </div>
                  </div>
                  {b.cpf && (
                    <a
                      href={`/beneficiario-detalhe/${b.cpf}`}
                      style={modalStyles.linkBtn}
                      title="Ver beneficiário"
                    >
                      <ExternalLink size={14} />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Modal styles ──────────────────────────────────────────────────────────────
const modalStyles = {
  overlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  modal: {
    background: '#fff',
    borderRadius: '12px',
    padding: '24px',
    width: '480px',
    maxWidth: '90vw',
    maxHeight: '70vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  badge: {
    background: '#e74c3c',
    color: '#fff',
    borderRadius: '12px',
    padding: '2px 10px',
    fontSize: '12px',
    fontWeight: '700',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#999',
    padding: '4px',
    borderRadius: '6px',
    display: 'flex',
  },
  listContainer: {
    overflowY: 'auto',
    flex: 1,
    borderRadius: '8px',
    border: '1px solid #eee',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 14px',
    gap: '10px',
    borderBottom: '1px solid #f0f0f0',
  },
  linkBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: '6px',
    background: '#f0f0f0',
    color: 'var(--brand, #20a490)',
    textDecoration: 'none',
    flexShrink: 0,
    transition: 'background 0.2s',
  },
};

// ── Empty state helper ─────────────────────────────────────────────────────
const EmptyState = ({ text }) => (
  <div style={styles.emptyState}>
    <BarChart3 size={36} color="#ddd" />
    <p style={{ color: '#bbb', margin: '8px 0 0 0', fontSize: '14px' }}>{text}</p>
  </div>
);

// ── Styles ─────────────────────────────────────────────────────────────────
const styles = {
  container: {
    padding: '24px',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
  },

  // Header
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '16px',
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  titulo: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#333',
    margin: 0,
  },
  subtitulo: {
    fontSize: '14px',
    color: '#666',
    margin: '4px 0 0 0',
  },

  // Filters
  filtros: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  periodButtons: {
    display: 'flex',
    gap: '4px',
  },
  periodBtn: {
    padding: '8px 14px',
    borderRadius: '8px',
    border: '1px solid #ddd',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  select: {
    padding: '10px 16px',
    borderRadius: '8px',
    border: '1px solid #ddd',
    fontSize: '14px',
    backgroundColor: 'white',
    cursor: 'pointer',
  },
  btnRefresh: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    borderRadius: '8px',
    border: '1px solid var(--brand, #20a490)',
    backgroundColor: 'white',
    color: 'var(--brand, #20a490)',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },

  // Stats grid (Row 1)
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '20px',
  },

  // Two-column layout
  twoCol: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
    gap: '20px',
    marginBottom: '20px',
  },

  // Generic card
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    padding: '20px 24px',
    marginBottom: '20px',
  },

  // Section header
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '16px',
    gap: '10px',
  },
  sectionHeaderBorder: {
    width: '4px',
    height: '22px',
    borderRadius: '2px',
    backgroundColor: 'var(--brand, #20a490)',
    flexShrink: 0,
  },
  sectionHeaderContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    margin: 0,
  },

  // Donut wrapper
  donutWrapper: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '8px',
  },

  // SLA
  slaContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '8px 0',
  },
  slaBigNumber: {
    fontSize: '56px',
    fontWeight: '800',
    lineHeight: 1,
    marginBottom: '4px',
  },
  slaLabel: {
    fontSize: '14px',
    color: '#666',
    margin: '0 0 20px 0',
  },
  slaBarTrack: {
    width: '100%',
    height: '12px',
    backgroundColor: '#f0f0f0',
    borderRadius: '6px',
    overflow: 'hidden',
    marginBottom: '20px',
  },
  slaBarFill: {
    height: '100%',
    borderRadius: '6px',
    transition: 'width 0.5s ease',
  },
  slaCountRow: {
    display: 'flex',
    gap: '32px',
    justifyContent: 'center',
  },
  slaCountItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  slaCountDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
  },
  slaCountLabel: {
    fontSize: '13px',
    color: '#666',
  },
  slaCountValue: {
    fontSize: '15px',
    fontWeight: '700',
    color: '#333',
  },

  // Horizontal bar chart
  hbarContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  hbarLegend: {
    display: 'flex',
    gap: '20px',
    marginBottom: '4px',
    flexWrap: 'wrap',
  },
  hbarLegendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    color: '#666',
  },
  hbarLegendDot: {
    width: '10px',
    height: '10px',
    borderRadius: '2px',
  },
  hbarRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  hbarLabel: {
    width: '160px',
    fontSize: '13px',
    color: '#444',
    flexShrink: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  hbarTrack: {
    flex: 1,
    height: '20px',
    backgroundColor: '#f0f0f0',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  hbarFill: {
    height: '100%',
    display: 'flex',
    borderRadius: '4px',
    overflow: 'hidden',
    transition: 'width 0.4s ease',
  },
  hbarSegment: {
    height: '100%',
  },
  hbarTotal: {
    width: '40px',
    textAlign: 'right',
    fontSize: '13px',
    fontWeight: '600',
    color: '#333',
    flexShrink: 0,
  },

  // Table
  tableWrapper: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  },
  th: {
    padding: '10px 12px',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: '600',
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '2px solid #f0f0f0',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '10px 12px',
    color: '#333',
    fontSize: '14px',
  },
  trEven: {
    backgroundColor: '#fafafa',
  },
  trOdd: {
    backgroundColor: 'white',
  },

  // Volume bar chart
  barChartContainer: {
    overflowX: 'auto',
    paddingBottom: '4px',
  },
  barChart: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '4px',
    height: '160px',
    minWidth: '100%',
    paddingBottom: '28px',
    position: 'relative',
  },
  barCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: '1 0 28px',
    maxWidth: '60px',
    height: '100%',
    position: 'relative',
  },
  barColInner: {
    flex: 1,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '2px',
  },
  barValue: {
    fontSize: '10px',
    color: '#999',
    lineHeight: 1,
  },
  bar: {
    width: '100%',
    backgroundColor: 'var(--brand, #20a490)',
    borderRadius: '3px 3px 0 0',
    minHeight: '2px',
    transition: 'height 0.4s ease',
  },
  barLabel: {
    position: 'absolute',
    bottom: -20,
    fontSize: '11px',
    color: '#555',
    whiteSpace: 'nowrap',
    transform: 'rotate(-45deg)',
    transformOrigin: 'top center',
    fontWeight: 500,
  },

  // Empty state
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '32px 16px',
  },
};

export default DashboardAtendimentos;
